import type { JsonValue } from "@note-app/contracts";
import { assertUuid } from "@note-app/domain";
import type {
  AttachmentRepository,
  AuthoritativeDataSource,
  AuthoritativeEntry,
  BlobStore,
  OperationContext,
} from "@note-app/knowledge-services";
import {
  reportProgress,
  throwIfCancelled,
} from "@note-app/knowledge-services";
import type { Repositories } from "@note-app/storage";

import type {
  NotebundleManifest,
  StreamingArchiveEntry,
  StreamingRestoreTargetPort,
} from "../notebundle.js";

const PAGE_SIZE = 200;

export interface RepositoryAuthoritativeDataSourceOptions {
  knowledgeBaseId: string;
  repositories: Repositories;
  attachments: AttachmentRepository;
  blobs: BlobStore;
  settings?: Readonly<Record<string, JsonValue>>;
}

/**
 * Enumerates every authoritative repository family in stable pages. It emits
 * individual folder/tag/revision and attachment records so semantic
 * `.notebundle` exports do not rely on a private database schema.
 */
export class RepositoryAuthoritativeDataSource
  implements AuthoritativeDataSource
{
  public constructor(
    private readonly options: RepositoryAuthoritativeDataSourceOptions,
  ) {
    assertUuid(options.knowledgeBaseId, "knowledgeBaseId");
  }

  async knowledgeBaseId(): Promise<string> {
    return this.options.knowledgeBaseId;
  }

  async *listAuthoritativeEntries(
    context?: OperationContext,
  ): AsyncIterable<AuthoritativeEntry> {
    let completed = 0;
    const emit = (entry: AuthoritativeEntry): AuthoritativeEntry => {
      completed += 1;
      reportProgress(context, {
        phase: "authoritative-enumeration",
        completed,
      });
      return entry;
    };

    let folderCursor: string | undefined;
    do {
      throwIfCancelled(context?.signal);
      const page = await this.options.repositories.folders.list({
        deletion: "all",
        limit: PAGE_SIZE,
        ...(folderCursor === undefined ? {} : { cursor: folderCursor }),
      });
      for (const folder of page.items) {
        yield emit(jsonEntry(`folders/${folder.id}.json`, "folder", folder));
      }
      folderCursor = page.nextCursor;
    } while (folderCursor !== undefined);

    let tagCursor: string | undefined;
    do {
      throwIfCancelled(context?.signal);
      const page = await this.options.repositories.tags.list({
        deletion: "all",
        limit: PAGE_SIZE,
        ...(tagCursor === undefined ? {} : { cursor: tagCursor }),
      });
      for (const tag of page.items) {
        yield emit(jsonEntry(`tags/${tag.id}.json`, "tag", tag));
      }
      tagCursor = page.nextCursor;
    } while (tagCursor !== undefined);

    let itemCursor: string | undefined;
    do {
      throwIfCancelled(context?.signal);
      const page = await this.options.repositories.items.list({
        deletion: "all",
        limit: PAGE_SIZE,
        ...(itemCursor === undefined ? {} : { cursor: itemCursor }),
      });
      for (const item of page.items) {
        yield emit(
          jsonEntry(`items/${item.id}.json`, "knowledge-item", item),
        );
        if (item.kind === "document") {
          const stored = await this.options.repositories.documents.load(item.id, {
            includeDeleted: true,
          });
          if (stored) {
            yield emit(
              jsonEntry(
                `documents/${item.id}.json`,
                "document",
                stored.document,
              ),
            );
          }
        }
        if (item.kind === "canvas") {
          const stored = await this.options.repositories.canvases.load(item.id, {
            includeDeleted: true,
          });
          if (stored) {
            yield emit(
              jsonEntry(`canvases/${item.id}.json`, "canvas", stored.canvas),
            );
          }
        }
        let revisionCursor: string | undefined;
        do {
          const revisions = await this.options.repositories.revisions.list(
            item.id,
            {
              limit: PAGE_SIZE,
              ...(revisionCursor === undefined
                ? {}
                : { cursor: revisionCursor }),
            },
          );
          for (const revision of revisions.items) {
            yield emit(
              jsonEntry(
                `revisions/${item.id}/${revision.id}.json`,
                "revision",
                revision,
              ),
            );
          }
          revisionCursor = revisions.nextCursor;
        } while (revisionCursor !== undefined);
      }
      itemCursor = page.nextCursor;
    } while (itemCursor !== undefined);

    for (const attachment of await this.options.attachments.list()) {
      throwIfCancelled(context?.signal);
      const {
        referenceCount: _referenceCount,
        unreferencedAt: _unreferencedAt,
        ...authoritativeAttachment
      } = attachment;
      yield emit(
        jsonEntry(
          `attachment-metadata/${attachment.id}.json`,
          "attachment-metadata",
          authoritativeAttachment,
        ),
      );
      yield emit({
        path: `attachments/${attachment.storageKey}`,
        kind: "attachment-blob",
        mediaType: attachment.mimeType,
        open: () => this.options.blobs.read(attachment.storageKey),
      });
    }

    yield emit(
      jsonEntry(
        "settings/workspace.json",
        "settings",
        this.options.settings ?? {},
      ),
    );
  }
}

export interface HostGrantedFile {
  token: string;
  name: string;
  byteLength: number;
  modifiedAt?: number;
}

export type HostArchiveInvoke = <Result>(
  command: string,
  args?: Record<string, unknown>,
) => Promise<Result>;

/**
 * User-authorized import/export archive stream. A token is only useful through
 * typed read/write commands and is released after the callback completes.
 */
export class TauriGrantedArchiveFile {
  public constructor(
    private readonly invoke: HostArchiveInvoke,
    private readonly chunkBytes = 1024 * 1024,
  ) {}

  async pickImport(): Promise<HostGrantedFile | null> {
    return this.invoke("host_pick_open_file", {
      request: { purpose: "importNotebundle" },
    });
  }

  async pickExport(suggestedName: string): Promise<HostGrantedFile | null> {
    return this.invoke("host_pick_save_file", {
      request: { purpose: "exportNotebundle", suggestedName },
    });
  }

  async *read(
    grant: HostGrantedFile,
    context?: OperationContext,
  ): AsyncIterable<Uint8Array> {
    let offset = 0;
    try {
      while (true) {
        throwIfCancelled(context?.signal);
        const result = await this.invoke<{
          bytes: readonly number[];
          offset: number;
          eof: boolean;
        }>("host_grant_read", {
          request: {
            token: grant.token,
            offset,
            length: this.chunkBytes,
          },
        });
        if (result.offset !== offset || result.bytes.length > this.chunkBytes) {
          throw new Error("Native archive reader returned an invalid chunk.");
        }
        const bytes = Uint8Array.from(result.bytes);
        if (bytes.byteLength > 0) {
          offset += bytes.byteLength;
          reportProgress(context, {
            phase: "archive-read",
            completed: offset,
            total: grant.byteLength,
          });
          yield bytes;
        }
        if (result.eof) return;
        if (bytes.byteLength === 0) {
          throw new Error("Native archive reader made no progress.");
        }
      }
    } finally {
      await this.release(grant.token);
    }
  }

  async write(
    grant: HostGrantedFile,
    chunks: AsyncIterable<Uint8Array>,
    context?: OperationContext,
  ): Promise<void> {
    let offset = 0;
    let committed = false;
    try {
      for await (const chunk of chunks) {
        throwIfCancelled(context?.signal);
        for (
          let chunkOffset = 0;
          chunkOffset < chunk.byteLength;
          chunkOffset += this.chunkBytes
        ) {
          const part = chunk.subarray(
            chunkOffset,
            chunkOffset + this.chunkBytes,
          );
          const nextOffset = await this.invoke<number>("host_grant_write", {
            request: {
              token: grant.token,
              offset,
              bytes: [...part],
            },
          });
          if (nextOffset !== offset + part.byteLength) {
            throw new Error("Native archive writer returned an invalid offset.");
          }
          offset = nextOffset;
          reportProgress(context, {
            phase: "archive-write",
            completed: offset,
          });
        }
      }
      throwIfCancelled(context?.signal);
      await this.invoke("host_grant_commit", {
        request: { token: grant.token },
      });
      committed = true;
    } finally {
      if (!committed) await this.release(grant.token);
    }
  }

  release(token: string): Promise<void> {
    return this.invoke("host_grant_release", {
      request: { token },
    });
  }
}

export interface HostRestoreTransport {
  createStage(knowledgeBaseId: string): Promise<string>;
  createEntry(stageId: string, path: string): Promise<string>;
  appendEntry(
    entryId: string,
    offset: number,
    bytes: Uint8Array,
  ): Promise<number>;
  commitEntry(entryId: string): Promise<void>;
  abandonEntry(entryId: string): Promise<void>;
  prepare(
    stageId: string,
    manifest: NotebundleManifest,
    bundleSha256: string,
  ): Promise<void>;
  publish(stageId: string): Promise<void>;
  abandon(stageId: string): Promise<void>;
}

/**
 * Streaming restore adapter for native hosts. Projection rebuilding remains an
 * injected application callback and is completed before `publish`.
 */
export class HostStreamingRestoreTarget
  implements StreamingRestoreTargetPort
{
  private readonly stageIds = new Map<string, string>();

  public constructor(
    private readonly transport: HostRestoreTransport,
    private readonly rebuild: (
      stageId: string,
      context?: OperationContext,
    ) => Promise<{
      searchItems: number;
      referenceItems: number;
      attachments: number;
    }>,
  ) {}

  async createStagingKnowledgeBase(knowledgeBaseId: string): Promise<void> {
    assertUuid(knowledgeBaseId, "knowledgeBaseId");
    this.stageIds.set(
      knowledgeBaseId,
      await this.transport.createStage(knowledgeBaseId),
    );
  }

  async writeStagedEntry(
    knowledgeBaseId: string,
    entry: StreamingArchiveEntry,
  ): Promise<void> {
    validateEntryPath(entry.path);
    const stageId = this.requireStage(knowledgeBaseId);
    const entryId = await this.transport.createEntry(stageId, entry.path);
    let offset = 0;
    try {
      for await (const chunk of entry.open()) {
        for (
          let chunkOffset = 0;
          chunkOffset < chunk.byteLength;
          chunkOffset += 1024 * 1024
        ) {
          const part = chunk.subarray(chunkOffset, chunkOffset + 1024 * 1024);
          const next = await this.transport.appendEntry(entryId, offset, part);
          if (next !== offset + part.byteLength) {
            throw new Error("Native restore target returned an invalid offset.");
          }
          offset = next;
        }
      }
      await this.transport.commitEntry(entryId);
    } catch (error) {
      try {
        await this.transport.abandonEntry(entryId);
      } catch (abandonError) {
        throw new AggregateError(
          [error, abandonError],
          "Restore entry write and cleanup both failed.",
        );
      }
      throw error;
    }
  }

  prepareStagedKnowledgeBase(
    knowledgeBaseId: string,
    manifest: NotebundleManifest,
    metadata: { restoredFromBundleSha256: string },
  ): Promise<void> {
    return this.transport.prepare(
      this.requireStage(knowledgeBaseId),
      manifest,
      metadata.restoredFromBundleSha256,
    );
  }

  rebuildStagedProjections(
    knowledgeBaseId: string,
    context?: OperationContext,
  ): Promise<{
    searchItems: number;
    referenceItems: number;
    attachments: number;
  }> {
    return this.rebuild(this.requireStage(knowledgeBaseId), context);
  }

  async publishStagedKnowledgeBase(knowledgeBaseId: string): Promise<void> {
    await this.transport.publish(this.requireStage(knowledgeBaseId));
    this.stageIds.delete(knowledgeBaseId);
  }

  async abandonStagedKnowledgeBase(
    knowledgeBaseId: string,
    _cause: unknown,
  ): Promise<void> {
    const stageId = this.stageIds.get(knowledgeBaseId);
    if (!stageId) return;
    await this.transport.abandon(stageId);
    this.stageIds.delete(knowledgeBaseId);
  }

  private requireStage(knowledgeBaseId: string): string {
    const stageId = this.stageIds.get(knowledgeBaseId);
    if (!stageId) {
      throw new Error(`No native restore stage for ${knowledgeBaseId}.`);
    }
    return stageId;
  }
}

function jsonEntry(
  path: string,
  kind: Exclude<AuthoritativeEntry["kind"], "attachment-blob">,
  value: unknown,
): AuthoritativeEntry {
  return {
    path,
    kind,
    mediaType: "application/json",
    async *open() {
      yield new TextEncoder().encode(JSON.stringify(value));
    },
  };
}

function validateEntryPath(value: string): void {
  if (
    value === "" ||
    value.startsWith("/") ||
    value.includes("\\") ||
    value.split("/").some((part) => part === "" || part === "." || part === "..")
  ) {
    throw new Error(`Unsafe restore entry path: ${value}`);
  }
}
